import * as Yup from 'yup';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Layouts } from 'react-grid-layout';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { Box, Chip, Collapse } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import ButtonLoading from '@mui/lab/LoadingButton';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import ActivityIndicator from 'components/ActivityIndicatorComponent';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { MainLayoutComponent } from 'components/LayoutComponent/SidebarLayout/MainLayoutComponent';
import { PATH_CONSTANT } from 'constant/PathConstant';
import { ChartItemComponentTypes, ChartViewEditor } from './components/ChartViewEditor';
import { ChartType, ChartViewType } from 'constant/ChartViewConstant';
import { CubeDefinitionDetails } from 'types/api/SystemTenant/AROCube/CubeBuilderTypes';
import { RequestCreateChartViewDefinition } from 'types/api/SystemTenant/AROChart/ChartDefinitionTypes';
import { isValidJSONString } from 'utils/Object';
import {
  useGetChartViewDetails,
  useGetChartViewInsightDropdown,
  useUpsertChartViewDefinition,
} from 'services/v1/SystemTenant/AROChart/ChartViewDefinitionService';
import {
  useGetStandardViewDefinitionCubeDropdown,
  useGetStandardViewDefinitionSubjectDropdown,
} from 'services/v1/SystemTenant/AROView/StandardViewDefinitionService';

// ── Styles ──

const autocompleteStyles = {
  '& .MuiOutlinedInput-root': {
    padding: '2px 4px',
    fontSize: 13,
  },
};

const textInputStyles = {
  '& .MuiOutlinedInput-root': {
    fontSize: 13,
  },
};

const sectionCardSx = {
  bgcolor: '#fff',
  borderRadius: 2,
  border: '1px solid #eef0f4',
  p: 2.5,
};

// ── Formik types & defaults ──

export interface CreateInsightViewDefinitionFormikInitialValues {
  name: string | null;
  subject: AutoCompleteItem | null;
  insightView: AutoCompleteItem | null;
  cube: AutoCompleteItem | null;
  type: string;
}
export const createInsightViewDefinitionFormikInitialValues: CreateInsightViewDefinitionFormikInitialValues = {
  name: null,
  subject: null,
  cube: null,
  insightView: null,
  type: ChartViewType.SINGLE_MEMBER_SELECTION,
};

export const createInsightViewDefinitionValidationSchema = Yup.object().shape({
  name: Yup.string().required('This field is required').nullable(),
  subject: Yup.object().nullable(),
  cube: Yup.object().required('This field is required').nullable(),
  insightView: Yup.object().required('This field is required').nullable(),
  type: Yup.string().required('This field is required').nullable(),
});

// ── Sub-components ──

function SectionLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <Typography variant='input-label' sx={{ fontWeight: 700, fontSize: 13, color: '#333', mb: 0.25 }}>
      {children}
      {required && <span style={{ color: '#e05555', marginLeft: 2 }}>*</span>}
    </Typography>
  );
}

function SectionHint({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant='caption' sx={{ color: '#999', fontSize: 11.5, lineHeight: 1.4, mb: 0.75 }}>
      {children}
    </Typography>
  );
}

function SectionTitle({ children, step }: { children: React.ReactNode; step: number }) {
  return (
    <Stack direction='row' alignItems='center' gap={1} mb={1.5}>
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          bgcolor: '#3B4797',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {step}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#3B4797' }}>{children}</Typography>
    </Stack>
  );
}

const CHART_VIEW_TYPE_OPTIONS = [
  {
    label: 'Single Member',
    description: 'View data for one member at a time',
    value: ChartViewType.SINGLE_MEMBER_SELECTION,
    icon: <PersonOutlineOutlinedIcon sx={{ fontSize: 22 }} />,
  },
  {
    label: 'Multiple Members',
    description: 'Compare data across multiple members',
    value: ChartViewType.MULTI_MEMBER_SELECTION,
    icon: <GroupOutlinedIcon sx={{ fontSize: 22 }} />,
  },
];

// ── Main Component ──

export default function CreateChartDefinition() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { chartViewId } = useParams<{ chartViewId: string }>();
  const [openChartViewEditor, setOpenChartViewEditor] = React.useState(false);
  const [chartViewComponents, setChartViewComponents] = React.useState<ChartItemComponentTypes[]>([]);
  const [layoutChart, setLayoutChart] = React.useState<Layouts>({ lg: [] });

  const formik = useFormik({
    initialValues: createInsightViewDefinitionFormikInitialValues,
    validationSchema: createInsightViewDefinitionValidationSchema,
    onSubmit: () => {},
  });

  const { data: chartViewDefinitionDetails, isLoading: isLoadingDetails } = useGetChartViewDetails(chartViewId);
  const { mutate: upsertChartViewDefinition, isLoading: isSubmitting } = useUpsertChartViewDefinition();

  const { data: subjectDropdownOptions, isLoading: isLoadingSubjectOptions } =
    useGetStandardViewDefinitionSubjectDropdown();

  const { data: insightViewDropdownOptions, isLoading: isLoadingInsightViewOptions } = useGetChartViewInsightDropdown();
  const { data: cubeDropdownOptions, isLoading: isLoadingCubeOptions } = useGetStandardViewDefinitionCubeDropdown(
    formik.values.subject?.value?.toString() ?? '',
  );

  const handleOnSubmit = () => {
    const mapChartIndexCodeWithChartId: { [key: string]: string } | undefined =
      chartViewDefinitionDetails?.data?.chartViewItems.reduce(
        (acc, item) => ({
          ...acc,
          [item.chartIndexCode]: item.id,
        }),
        {},
      );
    if (formik.values.name && mapChartIndexCodeWithChartId) {
      const payload: RequestCreateChartViewDefinition = {
        id: chartViewId,
        type: formik.values.type,
        name: formik.values.name,
        insightViewId: formik.values.insightView?.value?.toString() ?? '',
        cubeId: formik.values.cube?.value?.toString() ?? '',
        chartViewLayouts: JSON.stringify(layoutChart),
        chartItems: chartViewComponents.map((chart) => {
          const chartItem = {
            id: mapChartIndexCodeWithChartId[chart.id] ?? undefined,
            chartIndexCode: chart.id,
            name: chart.name,
            chartType: chart.chartType,
            description: chart.description,
            params: JSON.stringify({
              xAxis: chart.xAxis,
              yAxis: chart.yAxis,
            }),
          };

          if (chart.chartType === ChartType.GAUGE) {
            chartItem.params = JSON.stringify(chart.gaugeParams);
          }

          return chartItem;
        }),
      };
      upsertChartViewDefinition(payload, {
        onSuccess: () => {
          toast.success('Chart View Definition saved successfully');
          navigate(PATH_CONSTANT.SYSTEM.CHART.CHART_DEFINITION);
        },
      });
    }
  };

  const handleOnCancel = () => {
    navigate(PATH_CONSTANT.SYSTEM.CHART.CHART_DEFINITION);
  };

  const insightCubeDefinition: CubeDefinitionDetails | null = useMemo(() => {
    return (
      insightViewDropdownOptions?.data.mapInsightViewWithCubeDefinition[
        formik.values.insightView?.value?.toString() ?? ''
      ] ?? null
    );
  }, [formik.values.insightView, insightViewDropdownOptions?.data.mapInsightViewWithCubeDefinition]);

  const handleOnChartViewChange = (data: { charts: ChartItemComponentTypes[]; layouts: Layouts }) => {
    setChartViewComponents(data.charts);
    setLayoutChart(data.layouts);
  };

  useEffect(() => {
    if (insightCubeDefinition) {
      formik.setFieldValue('cube', {
        label: insightCubeDefinition.cubeName,
        value: insightCubeDefinition.id,
      });
    } else {
      formik.setFieldValue('cube', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insightCubeDefinition]);

  useEffect(() => {
    if (chartViewDefinitionDetails?.data) {
      formik.setValues({
        cube: chartViewDefinitionDetails.data.cube,
        insightView: chartViewDefinitionDetails.data.insightView,
        name: chartViewDefinitionDetails.data.name,
        type: chartViewDefinitionDetails.data.type,
        subject: null,
      });

      setChartViewComponents(
        chartViewDefinitionDetails.data?.chartViewItems.map((item) => {
          const chartItem = {
            id: item.chartIndexCode,
            name: item.name,
            chartType: item.chartType,
            description: item.description,
            xAxis: JSON.parse(item.params).xAxis,
            yAxis: JSON.parse(item.params).yAxis,
            gaugeParams: [],
          };

          if (item.chartType === ChartType.GAUGE) {
            chartItem.gaugeParams = isValidJSONString(item.params) ? JSON.parse(item.params) : [];
          }

          return chartItem;
        }),
      );
      const layouts = JSON.parse(chartViewDefinitionDetails.data.chartViewLayouts);
      setLayoutChart(layouts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartViewDefinitionDetails?.data]);

  const isAllFormValid = createInsightViewDefinitionValidationSchema.isValidSync(formik.values);
  const isLoadingOptionsData =
    isLoadingSubjectOptions || isLoadingCubeOptions || isLoadingInsightViewOptions || isLoadingDetails;
  const isDataSourceReady = Boolean(formik.values.insightView && formik.values.cube);

  const isStep1Done = Boolean(formik.values.name && formik.values.type);
  const isStep2Done = Boolean(formik.values.insightView && formik.values.cube);

  return (
    <MainLayoutComponent
      pageTitle='Edit Chart Definition'
      breadcrumbs={[t('SIDEBAR_MENU.DASHBOARD'), 'Chart Definition']}
    >
      {isLoadingOptionsData ? (
        <Stack height={450} bgcolor='#fff' borderRadius={2}>
          <ActivityIndicator />
        </Stack>
      ) : (
        <Stack gap={2}>
          {/* ── Section 1: Basic Info ── */}
          <Box sx={sectionCardSx}>
            <SectionTitle step={1}>Basic Information</SectionTitle>
            <Stack gap={2}>
              <Stack>
                <SectionLabel required>Chart View Name</SectionLabel>
                <SectionHint>A unique name to identify this chart view definition.</SectionHint>
                <TextField
                  sx={textInputStyles}
                  name='name'
                  hiddenLabel
                  value={formik.values.name}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  variant='outlined'
                  size='small'
                  fullWidth
                  placeholder='e.g. Revenue Overview Chart'
                />
              </Stack>

              <Stack>
                <SectionLabel required>Chart View Type</SectionLabel>
                <SectionHint>
                  Determines whether users can select a single member or compare multiple members at once.
                </SectionHint>
                <Stack direction='row' gap={1.5} mt={0.5}>
                  {CHART_VIEW_TYPE_OPTIONS.map((option) => {
                    const isSelected = formik.values.type === option.value;
                    return (
                      <Box
                        key={option.value}
                        onClick={() => formik.setFieldValue('type', option.value)}
                        sx={{
                          flex: 1,
                          border: isSelected ? '2px solid #3B4797' : '1px solid #dde1e6',
                          borderRadius: 2,
                          p: 1.5,
                          cursor: 'pointer',
                          bgcolor: isSelected ? '#f4f5ff' : '#fff',
                          transition: 'all 0.15s ease',
                          '&:hover': { borderColor: '#3B4797', bgcolor: '#fafafe' },
                        }}
                      >
                        <Stack direction='row' alignItems='flex-start' gap={1}>
                          {isSelected ? (
                            <CheckCircleOutlinedIcon sx={{ fontSize: 20, color: '#3B4797', mt: 0.1 }} />
                          ) : (
                            <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: '#ccc', mt: 0.1 }} />
                          )}
                          <Stack>
                            <Stack direction='row' alignItems='center' gap={0.5}>
                              {option.icon}
                              <Typography
                                sx={{ fontWeight: 700, fontSize: 13, color: isSelected ? '#3B4797' : '#333' }}
                              >
                                {option.label}
                              </Typography>
                            </Stack>
                            <Typography sx={{ fontSize: 11.5, color: '#888', mt: 0.25 }}>
                              {option.description}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Stack>
            </Stack>
          </Box>

          {/* ── Section 2: Data Source ── */}
          <Box sx={sectionCardSx}>
            <SectionTitle step={2}>Data Source</SectionTitle>
            <Stack gap={2}>
              <Stack>
                <SectionLabel required>Insight View</SectionLabel>
                <SectionHint>The insight view that provides the data for your charts.</SectionHint>
                <Autocomplete
                  options={insightViewDropdownOptions?.data.data ?? []}
                  disabled={isLoadingInsightViewOptions}
                  getOptionLabel={(option) => option.label}
                  sx={autocompleteStyles}
                  clearIcon={null}
                  value={formik.values.insightView}
                  onBlur={formik.handleBlur}
                  onChange={(e, value) => formik.setFieldValue('insightView', value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name='insightView'
                      placeholder='Search and select an insight view...'
                      error={formik.touched.insightView && Boolean(formik.errors.insightView)}
                      helperText={formik.touched.insightView && formik.errors.insightView}
                    />
                  )}
                />
              </Stack>

              <Collapse in={!insightCubeDefinition && Boolean(formik.values.insightView)}>
                <Stack>
                  <SectionLabel>Subject</SectionLabel>
                  <SectionHint>Select the subject (table) to filter available cubes.</SectionHint>
                  <Autocomplete
                    options={subjectDropdownOptions?.data ?? []}
                    getOptionLabel={(option) => option.label}
                    sx={autocompleteStyles}
                    clearIcon={null}
                    value={formik.values.subject}
                    onBlur={formik.handleBlur}
                    onChange={(e, value) => formik.setFieldValue('subject', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name='subject'
                        placeholder='Select Subject...'
                        error={formik.touched.subject && Boolean(formik.errors.subject)}
                        helperText={formik.touched.subject && formik.errors.subject}
                      />
                    )}
                  />
                </Stack>
              </Collapse>

              <Collapse in={Boolean(formik.values.insightView)}>
                <Stack>
                  <SectionLabel required>Cube</SectionLabel>
                  <SectionHint>The cube from which chart values are calculated.</SectionHint>
                  <Autocomplete
                    options={cubeDropdownOptions?.data ?? []}
                    disabled={isLoadingCubeOptions || Boolean(insightCubeDefinition)}
                    getOptionLabel={(option) => option.label}
                    sx={autocompleteStyles}
                    clearIcon={null}
                    value={formik.values.cube}
                    onBlur={formik.handleBlur}
                    onChange={(e, value) => formik.setFieldValue('cube', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name='cube'
                        placeholder='Select Cube...'
                        error={formik.touched.cube && Boolean(formik.errors.cube)}
                        helperText={formik.touched.cube && formik.errors.cube}
                      />
                    )}
                  />
                  {insightCubeDefinition && (
                    <Typography sx={{ fontSize: 11, color: '#42BB93', mt: 0.5 }}>
                      Auto-selected from insight view
                    </Typography>
                  )}
                </Stack>
              </Collapse>
            </Stack>
          </Box>

          {/* ── Section 3: Chart Layout ── */}
          <Box sx={sectionCardSx}>
            <Stack direction='row' justifyContent='space-between' alignItems='center' mb={1.5}>
              <SectionTitle step={3}>Chart Layout</SectionTitle>
              {chartViewComponents.length > 0 && (
                <Chip
                  label={`${chartViewComponents.length} chart${chartViewComponents.length > 1 ? 's' : ''}`}
                  size='small'
                  color='primary'
                  variant='outlined'
                  sx={{ fontWeight: 600, fontSize: 11 }}
                />
              )}
            </Stack>

            {!isDataSourceReady && (
              <Box
                sx={{
                  height: 200,
                  borderRadius: 2,
                  bgcolor: '#f8f9fb',
                  border: '1px dashed #dde1e6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Stack alignItems='center' gap={0.5}>
                  <TrendingUpIcon sx={{ fontSize: 32, color: '#ccc' }} />
                  <Typography sx={{ fontSize: 12, color: '#aaa', textAlign: 'center', maxWidth: 220 }}>
                    Complete the data source section to unlock the chart editor.
                  </Typography>
                </Stack>
              </Box>
            )}

            {isDataSourceReady && (
              <Stack gap={1.5}>
                <ButtonLoading
                  variant='main-table-panel-border'
                  startIcon={<TrendingUpIcon />}
                  onClick={() => setOpenChartViewEditor(true)}
                  fullWidth
                  sx={{ justifyContent: 'center' }}
                >
                  Open Chart View Editor
                </ButtonLoading>
              </Stack>
            )}
          </Box>

          {/* ── Progress summary + Actions ── */}
          <Box sx={{ ...sectionCardSx, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Stack direction='row' gap={1.5} alignItems='center'>
              <Chip
                size='small'
                label='Basic Info'
                color={isStep1Done ? 'success' : 'default'}
                variant={isStep1Done ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600, fontSize: 11 }}
              />
              <Chip
                size='small'
                label='Data Source'
                color={isStep2Done ? 'success' : 'default'}
                variant={isStep2Done ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600, fontSize: 11 }}
              />
              <Chip
                size='small'
                label={`Charts (${chartViewComponents.length})`}
                color={chartViewComponents.length > 0 ? 'success' : 'default'}
                variant={chartViewComponents.length > 0 ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600, fontSize: 11 }}
              />
            </Stack>
            <Stack direction='row' gap={1.5}>
              <ButtonLoading variant='main-table-panel-border' onClick={handleOnCancel}>
                Cancel
              </ButtonLoading>
              <ButtonLoading
                variant='main-table-panel'
                loading={isSubmitting}
                onClick={handleOnSubmit}
                disabled={!isAllFormValid}
              >
                Save Definition
              </ButtonLoading>
            </Stack>
          </Box>
        </Stack>
      )}
      <ChartViewEditor
        chartViewName={formik.values.name ?? ''}
        open={openChartViewEditor}
        initialValues={{
          charts: chartViewComponents,
          layouts: layoutChart,
        }}
        onClose={() => setOpenChartViewEditor(false)}
        onChanges={handleOnChartViewChange}
      />
    </MainLayoutComponent>
  );
}
